// Copyright (c) Ping. All rights reserved.
//
// Reload-loop breaker + breadcrumb. Several recovery paths hard-reload the page
// (`sessionBootstrap` device-mismatch / storage-corruption recovery,
// `forceCleanReload` on sign-out). If any of them fires repeatedly the tab
// "reloads in a loop". This wraps every app-initiated reload so that:
//   1. each reload records a {time, reason} breadcrumb in sessionStorage, and
//   2. once too many reloads happen inside a short window we REFUSE to reload
//      again and log the captured reasons loudly — breaking the loop AND
//      revealing exactly which path caused it (visible even if the console was
//      cleared on the prior navigation, because breadcrumbs persist).
//
// sessionStorage (not localStorage) so the breadcrumb is scoped to the tab and
// auto-clears when the tab closes.

const KEY = '__ping_reload_log';
const WINDOW_MS = 12_000;
const MAX_RELOADS_IN_WINDOW = 4;

interface ReloadCrumb {
  readonly t: number;
  readonly r: string;
}

function read(now: number): ReloadCrumb[] {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is ReloadCrumb =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as ReloadCrumb).t === 'number' &&
        typeof (e as ReloadCrumb).r === 'string' &&
        now - (e as ReloadCrumb).t < WINDOW_MS,
    );
  } catch {
    return [];
  }
}

/**
 * Reload the page, recording the reason. If more than `MAX_RELOADS_IN_WINDOW`
 * reloads have occurred within `WINDOW_MS`, REFUSE to reload (break the loop)
 * and surface the captured reasons. Returns `true` if it reloaded, `false` if
 * it broke the loop.
 */
export function safeReload(reason: string, now: number = Date.now()): boolean {
  const recent = read(now);
  recent.push({ t: now, r: reason });
  try {
    sessionStorage.setItem(KEY, JSON.stringify(recent.slice(-10)));
  } catch {
    /* best-effort */
  }

  if (recent.length > MAX_RELOADS_IN_WINDOW) {
    // Loud, persistent, and impossible to miss — lists every reload reason in
    // the window so the looping path is obvious without a fresh console capture.
     
    console.error(
      `[ping] RELOAD LOOP BROKEN after ${recent.length} reloads in ${WINDOW_MS / 1000}s — refusing further auto-reloads.`,
      { latest: reason, reasons: recent.map((e) => e.r) },
    );
    return false;
  }

   
  console.warn(`[ping] reload (#${recent.length} in window) reason=${reason}`);
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
  return true;
}

/** Clear the breadcrumb once the session is healthy (reached authenticated), so
 *  legitimate one-off reloads never accumulate toward the loop threshold. */
export function clearReloadGuard(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* best-effort */
  }
}

/** Read the current breadcrumb (for a boot-time diagnostic). Does not mutate. */
export function recentReloadReasons(now: number = Date.now()): string[] {
  return read(now).map((e) => e.r);
}
